import { useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import {
  isPerConnectionContact,
  isPerConnectionContactTickets,
} from "../../helpers/contactTicketMode";

// Quem liga da tela do chamado assume o chamado, com a mesma regra do envio de
// mensagem (ensureTicketOwnershipForSend no MessageInputCustom): `pending`
// assume; `closed` reabre e assume; `open` sem dono só recebe o dono; `open`
// com dono não é tocado.
//
// `assumeTicketForCall(ticket)` devolve o chamado em que a ligação deve sair
// (o próprio, um chamado novo do atendente ou o que já está aberto para o
// contato) ou null para desistir da ligação.
const useAssumeTicketOnCall = ({ isModal = false, onTicketUpdated } = {}) => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getSetting } = useContext(SettingsContext);

  // A ligação segue no CallContext mesmo se esta tela sair no meio do caminho;
  // só o que mexe na tela é cortado.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Chamado aberto do mesmo contato, como no findOpenTicketIdForContact do
  // envio: no modo "um por conexão" só conta o da mesma conexão.
  const findOpenTicketForContact = async (ticket) => {
    const { data } = await api.get("/tickets", {
      params: { contactId: ticket.contactId, status: "open", pageNumber: 1 },
    });

    const tickets = data?.tickets || [];
    const candidates =
      isPerConnectionContactTickets(getSetting) && isPerConnectionContact(ticket.contact)
        ? tickets.filter((t) => Number(t.whatsappId) === Number(ticket.whatsappId))
        : tickets;

    return candidates[0] || null;
  };

  const assumeTicketForCall = async (ticket) => {
    if (!ticket?.id || !user?.id) return ticket;

    const needsAssume =
      ticket.status === "pending" ||
      ticket.status === "closed" ||
      (ticket.status === "open" && !ticket.userId);

    if (!needsAssume) return ticket;

    try {
      // `queueId` fica fora pelo mesmo motivo do envio: é dele que dependem as
      // condições da mensagem automática de transferência (sendMsgTransfTicket).
      const { data } = await api.put(`/tickets/${ticket.id}`, {
        status: "open",
        userId: user.id,
        transferredBy: user.id,
      });

      if (isMountedRef.current) onTicketUpdated?.(data);
      return { ...ticket, ...data };
    } catch (err) {
      const code = err?.response?.data?.error;

      // Chamado de conexão excluída: só leitura.
      if (code === "ERR_TICKET_INACTIVE") {
        toastError(err);
        return null;
      }

      // Fila a que o atendente não tem acesso: abre um chamado novo dele na
      // mesma conexão e liga nele, deixando este onde está. No modo "um por
      // conexão" não existe segundo chamado na mesma conexão.
      if (code === "ERR_USER_NO_QUEUE_ACCESS" && !isPerConnectionContactTickets(getSetting)) {
        try {
          const { data: created } = await api.post("/tickets", {
            contactId: ticket.contactId,
            whatsappId: ticket.whatsappId,
            status: "open",
            userId: user.id,
          });

          toast.warn(i18n.t("calls.warnings.noQueueAccessNewTicket"));
          if (!isModal && isMountedRef.current && created?.uuid) {
            history.push(`/tickets/${created.uuid}`);
          }
          return { ...created, contact: created?.contact || ticket.contact };
        } catch (createErr) {
          toastError(createErr);
          return null;
        }
      }

      if (code === "ERR_OTHER_OPEN_TICKET") {
        // No modal (tela de Pendentes) a ligação sai no chamado que está na
        // tela, sem reabrir e sem trocar de chamado.
        if (isModal) {
          toast.warn(i18n.t("calls.warnings.otherOpenTicketCalledHere"));
          return ticket;
        }

        try {
          const openTicket = await findOpenTicketForContact(ticket);
          if (openTicket) return openTicket;
        } catch (searchErr) {
          console.error("[Call] Erro ao buscar chamado aberto do contato:", searchErr);
        }
      }

      toastError(err);
      return null;
    }
  };

  return { assumeTicketForCall };
};

export default useAssumeTicketOnCall;
